/**
 * バナーレイアウトの役割カタログ (docs/features/banner-layout-template-plan.md §1)。
 *
 * backend/services/banner_layout/roles.py と同じ表 (backend/tests/test_banner_layout.py が同期を検査する。
 * 1行1 kind の書式を崩さないこと)。color はガイドの枠線色、defaultW / defaultH は新規追加時の大きさ (キャンバス比)。
 */
export const BANNER_LAYOUT_KINDS = ['main_copy', 'text', 'cta', 'date', 'price', 'disclaimer', 'logo', 'product'] as const;
export type BannerLayoutKind = (typeof BANNER_LAYOUT_KINDS)[number];

export type BannerLayoutRole = {
  label: string;
  color: string;
  defaultW: number;
  defaultH: number;
  hasText: boolean;
};

export const BANNER_LAYOUT_ROLES: Record<BannerLayoutKind, BannerLayoutRole> = {
  main_copy: { label: 'メインコピー', color: '#E5484D', defaultW: 0.5, defaultH: 0.18, hasText: true },
  text: { label: 'テキスト', color: '#30A46C', defaultW: 0.45, defaultH: 0.1, hasText: true },
  cta: { label: 'CTA', color: '#F76B15', defaultW: 0.28, defaultH: 0.1, hasText: true },
  date: { label: '日付', color: '#0090FF', defaultW: 0.3, defaultH: 0.08, hasText: true },
  price: { label: '価格', color: '#E09B00', defaultW: 0.3, defaultH: 0.1, hasText: true },
  disclaimer: { label: '注意書き', color: '#6E56CF', defaultW: 0.5, defaultH: 0.06, hasText: true },
  logo: { label: 'ロゴ', color: '#D6409F', defaultW: 0.18, defaultH: 0.1, hasText: false },
  product: { label: '商品', color: '#12A594', defaultW: 0.4, defaultH: 0.5, hasText: false },
};

/** 番号を振る前の役割名。text は利用者のラベル (空なら「テキスト」) */
export function baseLabel(element: { kind: BannerLayoutKind; label?: string }): string {
  if (element.kind === 'text') {
    const label = (element.label ?? '').trim();
    if (label) return label;
  }
  return BANNER_LAYOUT_ROLES[element.kind].label;
}

/** ガイド / プロンプトと同じ役割名。同じ名前が複数あれば「メインコピー1」「メインコピー2」と連番 (roles.py element_labels と同じ規則) */
export function elementLabels(elements: { kind: BannerLayoutKind; label?: string }[]): string[] {
  const bases = elements.map(baseLabel);
  const totals = new Map<string, number>();
  bases.forEach((b) => totals.set(b, (totals.get(b) ?? 0) + 1));
  const seen = new Map<string, number>();
  return bases.map((b) => {
    if ((totals.get(b) ?? 0) <= 1) return b;
    const n = (seen.get(b) ?? 0) + 1;
    seen.set(b, n);
    return `${b}${n}`;
  });
}
